import { AbortedError, FeatureDisabledError, KnownError } from '../types/errors';
import { promisefy } from '../utils/promisefy';
import { AudioCompositionInfo, OutputInfo, ReplayBufferOutputInfo, VideoCompositionInfo } from '../types/obs';
import { AudioCompositionsService, RecordingSettings } from './AudioCompositionsService';
import PQueue from 'p-queue';
import { getIsReplayBufferEnabled } from '../constants/common';
import { HotkeyPersistenceService } from './HotkeyPersistenceService';
import { AuthContextProvider } from '@streamelements/client-sdk-js';

type ReplayBufferEvents = {
    onSavedToLocalFile?: (data: { filePath: string, outputId: string }) => void;
    onSaveError?: (data: { message: string }) => void;
    onCreateError?: (data: { message: string }) => void;
    onCreated?: () => void;
    onDisposed?: () => void;
    onStarted?: () => void;
    onStopped?: () => void;
    onFeatureDisabled?: () => void;
};

export interface ReplayBuffer {
    saveBuffer(): Promise<{ filePath: string, outputId: string }>;
    isActive(): Promise<boolean>;
}

class ReplayBufferImpl implements ReplayBuffer {
    private videoCompositionId: string;
    private audioCompositionId: string;
    private abortController: AbortController;
    private events: ReplayBufferEvents;
    private isBusy: boolean = false;
    private isSuspended: boolean = false;
    private queue = new PQueue({ concurrency: 1, autoStart: true });
    private authContextProvider: AuthContextProvider;

    constructor(data: { authContextProvider: AuthContextProvider; videoCompositionId: string } & ReplayBufferEvents) {
        this.authContextProvider = data.authContextProvider;
        this.videoCompositionId = data.videoCompositionId;
        this.audioCompositionId = `replay_buffer_${data.videoCompositionId}_audio`;
        this.events = data;
        this.abortController = new AbortController();

        this.init();
    }

    public async dispose(): Promise<void> {
        this.abortController.abort();

        await this.freeResources();

        await this.queue.add(async () => {
            this.events.onDisposed?.();
        });
    }

    public async suspend(): Promise<void> {
        this.isSuspended = true;

        await this.freeResources();
    }

    public async resume(): Promise<void> {
        this.isSuspended = false;

        await this.init();
    }

    public async isActive(): Promise<boolean> {
        const output = await this.getOutput();

        if (this.abortController.signal.aborted) {
            return false;
        }

        return !!output?.isActive;
    }

    public async setEvents(events: ReplayBufferEvents): Promise<void> {
        this.events = events;
    }

    public async saveBuffer(): Promise<{ filePath: string, outputId: string }> {
        const localAbortController = new AbortController();
        let watchdogTimer: NodeJS.Timeout | null = null;

        const stopWatchdogTimer = () => {
            if (watchdogTimer) {
                clearTimeout(watchdogTimer);
                watchdogTimer = null;
            }
        };

        try {
            let resolvePromise: ((data: { filePath: string, outputId: string }) => void) | null = null;
            let rejectPromise: ((reason?: Error) => void) | null = null;
    
            const promise = new Promise<{ filePath: string, outputId: string }>((resolve, reject) => {
                resolvePromise = resolve;
                rejectPromise = reject;
            });

            if (this.isBusy) {
                throw new KnownError('Save is already in progress');
            }

            const output = await this.getOutput();

            if (!output) {
                throw new KnownError('ReplayBuffer output not found');
            }

            const handleReplayBufferSaved = (event: Event) => {
                const e = event as CustomEvent;
                const data = e.detail as { filePath: string, outputId: string };
    
                if (output.id !== data.outputId) {
                    return;
                }

                localAbortController.abort();
                stopWatchdogTimer();

                this.isBusy = false;

                resolvePromise?.({ filePath: data.filePath, outputId: data.outputId });
            };
        
            const handleReplayBufferSaveError = async (event: Event) => {
                const e = event as CustomEvent;
                const data = e.detail as { outputId: string };
        
                localAbortController.abort();
                stopWatchdogTimer();

                if (output.id !== data.outputId) {
                    return;
                }
        
                if (this.abortController.signal.aborted) {
                    return;
                }
        
                this.isBusy = false;

                const errorOutput = await this.getOutput();
               
                const message = (errorOutput as unknown as OutputInfo)?.stats?.lastErrorMessage || 'Unknown error';

                rejectPromise?.(new KnownError(message));
            };

            const startWatchdogTimer = () => {
                stopWatchdogTimer();

                watchdogTimer = setTimeout(() => {
                    localAbortController.abort();
                    stopWatchdogTimer();

                    this.isBusy = false;

                    const message = 'Timeout expired while waiting for ReplayBuffer save';

                    rejectPromise?.(new KnownError(message));
                }, 15000);
            };

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            window.addEventListener('hostReplayBufferOutputSavedToLocalFile', handleReplayBufferSaved, { signal: localAbortController.signal });
            window.addEventListener('hostReplayBufferOutputError', handleReplayBufferSaveError, { signal: localAbortController.signal });

            this.abortController.signal.addEventListener('abort', () => {
                localAbortController.abort();
            }, { signal: localAbortController.signal });

            const triggerResult = await promisefy(window.host.triggerReplayBufferOutputSaveById)(output.id);

            if (!triggerResult) {
                const errOutput = await this.getOutput();

                throw new KnownError(
                    `Failed to start ReplayBuffer save: ${(errOutput as unknown as OutputInfo)?.stats?.lastErrorMessage ?? 'please check OBS recording output settings'}`
                );
            }

            startWatchdogTimer();

            const promiseResult = await promise;

            stopWatchdogTimer();

            return promiseResult;
        } catch (error) {
            stopWatchdogTimer();
            localAbortController.abort();

            await this.queue.add(async () => {
                this.events.onSaveError?.({ message: (error as Error).message || 'Could not save replay buffer' });
            });

            throw error;
        }
    }

    private async getOutput(): Promise<ReplayBufferOutputInfo | undefined> {
        const allOutputs = await promisefy(window.host.getAllReplayBufferOutputs)();

        return Object.values(allOutputs).find((item) => item.videoCompositionId === this.videoCompositionId);
    }

    private async freeResources(): Promise<void> {
        const output = await this.getOutput();

        if (output) {
            await promisefy(window.host.disableReplayBufferOutputsByIds)([output.id]);

            await this.dispatchOutputActiveStateChangedEvent(output);

            await promisefy(window.host.removeReplayBufferOutputsByIds)([output.id]);
        }

        await promisefy(window.host.removeAudioCompositionsByIds)([this.audioCompositionId]);
    }

    private async getVideoComposition(): Promise<VideoCompositionInfo | undefined> {
        return Object.values(
            await promisefy(window.host.getAllVideoCompositions)()
        ).find((item) => item.id === this.videoCompositionId);
    }

    private async getAudioComposition(): Promise<AudioCompositionInfo | undefined> {
        return Object.values(
            await promisefy(window.host.getAllAudioCompositions)()
        ).find((item) => item.id === this.audioCompositionId);
    }

    private async getRecordingSettings(videoComposition: VideoCompositionInfo): Promise<RecordingSettings> {
        const recordingSettings = await AudioCompositionsService.getRecordingSettings({
            audioCompositionId: videoComposition.id, // This is weird but requires refactoring across the code base to get right
        });

        return recordingSettings;
    }

    private async createAudioComposition(videoComposition: VideoCompositionInfo, recordingSettings: RecordingSettings) {
        const availableAudioEncoders = await promisefy(window.host.getAvailableAudioEncoders)();
        const audioEncoderInfo = availableAudioEncoders.find(
          (encoder) => encoder.id === (recordingSettings?.recordingAudioEncoderClass ?? 'ffmpeg_aac')
        )!;
        const audioEncoderProperties = await promisefy(window.host.getEncoderProperties)(audioEncoderInfo);
  
        if (!audioEncoderProperties) {
          await this.freeResources();
  
          throw new KnownError(`Audio encoder not found: ${audioEncoderInfo.id}`);
        }

        audioEncoderProperties.settings.bitrate = recordingSettings?.recordingBitrate ?? 128;

        const audioComposition = await promisefy(window.host.addAudioComposition)({
            id: this.audioCompositionId,
            name: `${videoComposition.name} - StreamElements Replay Buffer Audio`,
            recordingAudioEncoder: audioEncoderProperties,
            streamingAudioEncoder: audioEncoderProperties,
            audioTracks: Array.from({ length: 6 }, (_, index) => ({ // TODO: Pull this data from SE.Live API
                id: index,
                name: `Track ${index + 1}`,
            })),
        });

        if (!audioComposition) {
            await this.freeResources();

            throw new KnownError(
                `Failed to create audio composition. Are this canvas' recording settings correct? ${void 0}`
            );
        }

        return audioComposition;
    }

    private dispatchOutputActiveStateChangedEvent(output: ReplayBufferOutputInfo): void {
        if (output.isActive) {
            this.queue.add(async () => {
                await HotkeyPersistenceService.startTrackingHotkey(this.authContextProvider, {
                    registererTypeName: 'output',
                    registererName: output.id,
                    name: 'ReplayBuffer.Save',
                });
       
                this.events.onStarted?.();
            });
        } else {
            this.queue.add(async () => {
                await HotkeyPersistenceService.stopTrackingHotkey({
                    registererTypeName: 'output',
                    registererName: output.id,
                    name: 'ReplayBuffer.Save',
                });

                this.events.onStopped?.();
            });
        }
     }

    private async handleOutputActiveStateChanged(event: Event): Promise<void> {
        const e = event as CustomEvent;
        const data = e.detail as { outputId: string };

        const output = await this.getOutput();

        if (!output) {
            return;
        }

        if (data.outputId !== output.id) {
            return;
        }

        this.dispatchOutputActiveStateChangedEvent(output);
    }

    private async init(): Promise<void> {
        try {
            if (this.isSuspended)
                return;

            const videoComposition = await this.getVideoComposition();

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            if (!videoComposition) {
                throw new KnownError(`Video composition ${this.videoCompositionId} not found`);
            }

            const recordingSettings = await this.getRecordingSettings(videoComposition);

            if (!recordingSettings.useReplayBuffer) {
                throw new FeatureDisabledError('Replay Buffer is disabled on this canvas');
            }

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            const existingOutput = await this.getOutput();

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            if (existingOutput) {
                await this.queue.add(async () => {
                    this.events.onCreated?.();

                    this.dispatchOutputActiveStateChangedEvent(existingOutput);
                });
                return;
            }

            let audioComposition = await this.getAudioComposition();

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            if (!audioComposition) {
                audioComposition = await this.createAudioComposition(videoComposition, recordingSettings);
            }

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            const defaultRecordingOutput = (await promisefy(window.host.getAllRecordingOutputs)())['default'] ?? {};

            const outputObsDisplayName = `Canvas Backtrack (Replay Buffer): ${videoComposition.name}`;

            const result = await promisefy(window.host.addReplayBufferOutput)({
              ...defaultRecordingOutput,
              id: outputObsDisplayName,
              videoCompositionId: this.videoCompositionId,
              audioCompositionId: audioComposition.id,
              audioTracks: recordingSettings?.recordingAudioTracks ?? [0],
              recordingSettings: {
                settings: {
                  fileNameFormat: `${videoComposition.name} - ${recordingSettings.recordingFileFormat ?? '%CCYY-%MM-%DD - %hh:%mm:%ss'} - Replay`,
                  splitAtMaximumDurationSeconds: recordingSettings.replayBufferMaxSeconds,
                },
              },
              name: `${videoComposition.name} - StreamElements Replay Buffer`,
              isEnabled: true,
            });

            if (this.abortController.signal.aborted) {
                throw new AbortedError();
            }

            if (!result) {
                throw new KnownError(
                    `Failed to create replay buffer output. Are this canvas' recording settings correct? ${void 0}`
                );
            }

            await this.queue.add(async () => {
                this.events.onCreated?.();

                await this.dispatchOutputActiveStateChangedEvent(result as unknown as ReplayBufferOutputInfo);
            });

            window.addEventListener(
                'hostReplayBufferOutputStarted',
                this.handleOutputActiveStateChanged.bind(this),
                { signal: this.abortController.signal }
            );

            window.addEventListener(
                'hostReplayBufferOutputStopped',
                this.handleOutputActiveStateChanged.bind(this),
                { signal: this.abortController.signal }
            );

            window.addEventListener(
                'hostReplayBufferOutputSavedToLocalFile', async (event: Event) => {
                    const e = event as CustomEvent;
                    const data = e.detail as { filePath: string, outputId: string };
             
                    await this.queue.add(async () => {
                        const output = await this.getOutput();

                        if (output?.id !== data.outputId) {
                            return;
                        }

                        this.events.onSavedToLocalFile?.({ filePath: data.filePath, outputId: data.outputId });
                    });       
                },
                { signal: this.abortController.signal }
            );
        } catch (error) {
            await this.freeResources();

            if (error instanceof FeatureDisabledError) {
                await this.queue.add(async () => {
                    this.events.onFeatureDisabled?.();
                });
            } else if (!(error instanceof AbortedError)) {
                await this.queue.add(async () => {
                    this.events.onCreateError?.({ message: (error as Error).message });
                });
            }
        }
    }
}

export class ReplayBufferService {
    private static videoCanvasReplayBuffers: Map<string, ReplayBufferImpl> = new Map();
    private static queue = new PQueue({ concurrency: 1 });

    public static async createOrGetReplayBufferForVideoComposition(data: { authContextProvider: AuthContextProvider; videoCompositionId: string } & ReplayBufferEvents): Promise<ReplayBuffer> {
        if (!await getIsReplayBufferEnabled()) {
            throw new FeatureDisabledError('Replay buffer is not enabled');
        }

        return await this.queue.add<ReplayBuffer>(async (): Promise<ReplayBuffer> => {
            const existing = this.videoCanvasReplayBuffers.get(data.videoCompositionId);

            if (existing) {
                await existing.setEvents(data);

                return existing;
            }

            const created = new ReplayBufferImpl(data);

            this.videoCanvasReplayBuffers.set(data.videoCompositionId, created);

            return created;
        }) as ReplayBuffer;
    }

    public static async destroyExistingReplayBufferForVideoComposition({ videoCompositionId }: { videoCompositionId: string }): Promise<void> {
        if (!await getIsReplayBufferEnabled()) {
            throw new FeatureDisabledError('Replay buffer is not enabled');
        }

        await this.queue.add(async (): Promise<void> => {
            const existing = this.videoCanvasReplayBuffers.get(videoCompositionId);

            if (existing) {
                await existing.dispose();

                this.videoCanvasReplayBuffers.delete(videoCompositionId);
            }
        });
    }

    public static async suspendExistingReplayBufferForVideoComposition({ videoCompositionId }: { videoCompositionId: string }) {
        if (!await getIsReplayBufferEnabled()) {
            throw new FeatureDisabledError('Replay buffer is not enabled');
        }

        return await this.queue.add(async (): Promise<void> => {
            const existing = this.videoCanvasReplayBuffers.get(videoCompositionId);

            if (!existing)
                return;

            await existing.suspend();
        });
    }

    public static async resumeExistingReplayBufferForVideoComposition({ videoCompositionId }: { videoCompositionId: string }) {
        if (!await getIsReplayBufferEnabled()) {
            throw new FeatureDisabledError('Replay buffer is not enabled');
        }

        return await this.queue.add(async (): Promise<void> => {
            const existing = this.videoCanvasReplayBuffers.get(videoCompositionId);

            if (!existing)
                return;

            await existing.resume();
        });        
    }
}
